import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import SectionHeader from './components/SectionHeader';
import PackageGrid from './components/PackageGrid';
import { packageOptions } from './data/packageOptions';
import { usePackageSelection } from './hooks/usePackageSelection';
import { backgroundOptions } from './data/backgroundOptions';
import AgencySection from './components/AgencySection';
import HeaderSection from '../components/HeaderSection';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ManageGoPlanStepProps {
  onNext: (selectedPackageId?: string) => void;
  onPackageSelected: (packageId: string) => void;
}

const ManageGoPlanStep: React.FC<ManageGoPlanStepProps> = ({ 
  onNext, 
  onPackageSelected 
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { selectedPackage, initialPackage, handlePackageSelect, updateContextPackage } = usePackageSelection();
  
  const [selectedBackground, setSelectedBackground] = useState(0);
  
  const handleContinue = useCallback(() => {
    if (selectedPackage) {
      console.log('Continue clicked with package:', selectedPackage);
      updateContextPackage(selectedPackage);
      onPackageSelected(selectedPackage);
      onNext(selectedPackage);
    }
  }, [selectedPackage, onNext, onPackageSelected, updateContextPackage]);
  
  const currentBg = backgroundOptions[selectedBackground];
  
  const showBackButton = initialPackage !== null;

  const handleBackClick = useCallback(() => {
    console.log('Back button clicked, navigating to step 1');
    onNext(initialPackage);
  }, [onNext, initialPackage]);
  
  const handleBackToDashboard = useCallback(() => {
    console.log('Back to Dashboard button clicked');
    navigate('/');
  }, [navigate]);
  
  const headerTitle = initialPackage !== null ? "Update Your GoPlan" : "Pick Your GoPlan";
  
  const buttonText = initialPackage !== null ? "Save Changes" : "Continue To Final Step";
  
  const showContinueButton = 
    selectedPackage !== null && 
    (initialPackage === null || selectedPackage !== initialPackage);
  
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-4">
        <div className="flex justify-center md:col-span-1 md:pt-0 pt-6">
          {showBackButton ? (
            <Button 
              variant="ghost" 
              size="sm"
              className="flex items-center text-muted-foreground hover:text-foreground"
              onClick={handleBackClick}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to GoCampaign
            </Button>
          ) : (
            <Button 
              variant="ghost" 
              size="sm"
              className="flex items-center text-muted-foreground hover:text-foreground"
              onClick={handleBackToDashboard}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Dashboard
            </Button>
          )}
        </div>
        
        <div className="md:col-span-1 md:text-center">
          <HeaderSection
            title={headerTitle}
            subtitle="Select the GoPlan that best fits your business needs"
            className="pt-6 md:pt-12 text-center"
          />
        </div>
        
        {showContinueButton && !isMobile && (
          <div className="hidden md:flex md:items-center md:justify-center md:col-span-1">
            <Button
              onClick={handleContinue}
              className="px-6 py-5 text-base flex items-center"
              type="button"
            >
              {buttonText}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      <div className="p-0">
        <div className="space-y-3">
          <PackageGrid 
            packages={packageOptions}
            selectedPackage={selectedPackage}
            initialPackage={initialPackage}
            onPackageSelect={handlePackageSelect}
          />
        </div>
      </div>

      <div className="mt-6 mb-24 md:mb-6">
        <AgencySection currentBg={currentBg} />
      </div>
      
      {showContinueButton && isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200 shadow-lg z-10">
          <Button
            onClick={handleContinue}
            className="w-full px-8 py-6 text-lg flex items-center justify-center"
            type="button"
          >
            {buttonText}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      )}
    </>
  );
};

export default ManageGoPlanStep;
